// stocai_frontend/src/components/HomePage/components/widgets/Module7/Steps/ThinkWhatMadeThisPossibleStep.jsx

import React, { useState } from "react";
import { motion } from "framer-motion";
import bgFutureImg from "../../../../../../assets/bg_future.png";
import futurePersonImg from "../../../../../../assets/future_person_2.png";
import futureTargetImg from "../../../../../../assets/concentric.png";
import line2Img from "../../../../../../assets/line2.png";
import line3Img from "../../../../../../assets/line3.png";

const ThinkWhatMadeThisPossibleStep = ({
  // These now come from the parent (VisualizeFuture)
  skipLevelManagerNotice,
  setSkipLevelManagerNotice,
  directManagerNotice,
  setDirectManagerNotice,
  thirdPerspectiveNotice,
  setThirdPerspectiveNotice,

  // callback to continue to next widget-section
  onNextStep,
}) => {
  // UI-only stage (does NOT persist; parent persists when submitting final)
  const [stage, setStage] = useState(0);

  const showDirectBox = stage >= 1;
  const showThirdBox = stage >= 2;

  const handleContinue = () => {
    // Stage transitions
    if (stage === 0) {
      setStage(1);
      return;
    }
    if (stage === 1) {
      setStage(2);
      return;
    }

    // Final stage → pass collected data to parent to save + proceed
    if (stage === 2) {
      if (onNextStep) {
        onNextStep({
          skipLevelManagerNotice,
          directManagerNotice,
          thirdPerspectiveNotice,
        });
      }
    }
  };

  return (
    <div className="w-full h-full bg-white flex flex-col items-center">
      {/* HEADER */}
      <div className="mb-2 flex flex-col items-center text-center px-4">
        <h1 className="text-[28px] md:text-[32px] font-[500] leading-[135%] text-[#000000] mb-2 font-[Gotham]">
          Think what made this possible
        </h1>

        <p className="text-[19px] font-[400] leading-[160%] text-[#5A5A5A] font-[Gotham]">
          What did others see in you?
        </p>

        <p className="text-[19px] font-[400] leading-[160%] text-[#5A5A5A] font-[Gotham]">
          Your promotion was validated by multiple perspectives
        </p>
      </div>

      {/* ILLUSTRATION + TEXTBOXES */}
      <div className="w-full flex justify-center mb-10">
        <div className="relative w-[760px] h-[520px]">
          {/* BACKGROUND */}
          <img
            src={bgFutureImg}
            alt="Boardroom background"
            className="absolute inset-0 m-auto w-[758px] h-[874px] object-contain pointer-events-none"
          />

          {/* PERSON */}
          <motion.img
            src={futurePersonImg}
            alt="Future you"
            className="absolute w-[428px] h-[639px] bottom-[-160px] left-40 object-contain pointer-events-none z-10"
            initial={{ opacity: 1, scale: 1 }}
            animate={{
              scale: 1.25,
              transition: { delay: 2, type: "spring", stiffness: 90, damping: 18 },
            }}
          />

          {/* TARGET ICON */}
          <motion.div
            className="absolute z-20 w-[246px] h-[120px] top-[165px] left-1/2 -translate-x-[80px]"
            initial={{ opacity: 0, scale: 0.4, rotate: -6 }}
            animate={{
              opacity: 1,
              scale: 1,
              rotate: 0,
              transition: { delay: 2.1, type: "spring", stiffness: 150, damping: 14 },
            }}
          >
            <img
              src={futureTargetImg}
              alt="Concentric target"
              className="w-full h-full left-1/2 -translate-x-[50px] object-contain pointer-events-none select-none"
              draggable={false}
            />
            <div className="absolute top-[-15px] left-1/2 -translate-x-[180px]">
              <span className="text-white text-[18px] leading-[135%] font-medium font-[Gotham]">
                Future You
              </span>
            </div>
          </motion.div>

          {/* CONNECTOR LINE (left) */}
          <motion.img
            src={line2Img}
            className="absolute z-10 top-[263px] left-[147px] w-[200px] h-[80px] object-contain"
            initial={{ opacity: 0 }}
            animate={{ opacity: showDirectBox ? 1 : 0, transition: { duration: 0.4 } }}
          />

          {/* LEFT BOX — DIRECT MANAGER */}
          <motion.div
            className="absolute z-20 top-[215px] left-[-200px] w-[392px]"
            initial={{ opacity: 0, x: -20 }}
            animate={{
              opacity: showDirectBox ? 1 : 0,
              x: showDirectBox ? 0 : -20,
              transition: { duration: 0.45 },
            }}
          >
            <p className="font-[Gotham] font-[700] text-[16px] leading-[165%] text-[#19AFAE] mb-3">
              What did your Direct manager notice?
            </p>

            <div className="w-[392px] h-[187px] rounded-[5px] border border-[#54B0AF] bg-white shadow-[0_0_24px_#54B0AF66]">
              <textarea
                value={directManagerNotice}
                onChange={(e) => setDirectManagerNotice(e.target.value)}
                placeholder="Ex: Your manager recognised your leadership…"
                className="w-full h-full px-[18px] py-[18px] outline-none rounded-[5px] text-[18px] font-[325] font-[Gotham] leading-[165%] text-[#323232] placeholder:text-[#9B9B9B] resize-none"
              />
            </div>

            {stage === 1 && (
              <motion.button
                type="button"
                onClick={handleContinue}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center justify-between w-[195px] h-[57px] bg-[#0D1111] text-white rounded-[20px] px-[20px]"
              >
                Go to Next
                <span className="ml-3 flex items-center justify-center w-[18px] h-[18px] bg-white text-black rounded-full">
                  <svg width={10} height={10} viewBox="0 0 20 20">
                    <path d="M8 5l4 5-4 5" stroke="#151A1F" strokeWidth="2" />
                  </svg>
                </span>
              </motion.button>
            )}
          </motion.div>

          {/* RIGHT BOX — SKIP LEVEL MANAGER */}
          <motion.div
            className="absolute z-30 top-[35px] left-[575px] w-[392px]"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0, transition: { delay: 2.2, duration: 0.45 } }}
          >
            <p className="font-[Gotham] font-[700] text-[16px] text-[#186D6C] mb-3">
              What did your Skip level manager notice?
            </p>

            <div className="w-[392px] h-[187px] border border-[#186D6C] rounded-[5px] bg-white shadow-[0_0_24px_#186D6C66]">
              <textarea
                value={skipLevelManagerNotice}
                onChange={(e) => setSkipLevelManagerNotice(e.target.value)}
                placeholder="Ex: Your skip-level manager noticed that you…"
                className="w-full h-full px-[18px] rounded-[5px] py-[18px] font-[325] outline-none text-[18px] font-[Gotham] leading-[165%] text-[#323232] placeholder:text-[#9B9B9B] resize-none"
              />
            </div>

            {stage === 0 && (
              <motion.button
                type="button"
                onClick={handleContinue}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center font-gotham font-medium justify-between w-[195px] h-[57px] bg-[#0D1111] text-white rounded-[20px] px-[20px]"
              >
                Go to Next
                <span className="ml-3 flex items-center justify-center w-[18px] h-[18px] bg-white text-black rounded-full">
                  <svg width={10} height={10} viewBox="0 0 20 20">
                    <path d="M8 5l4 5-4 5" stroke="#151A1F" strokeWidth="2" />
                  </svg>
                </span>
              </motion.button>
            )}
          </motion.div>

          {/* CONNECTOR TO THIRD BOX */}
          <motion.img
            src={line3Img}
            className="absolute z-20 top-[245px] left-[380px] w-[197px] h-[207px] object-contain"
            initial={{ opacity: 0 }}
            animate={{ opacity: showThirdBox ? 1 : 0, transition: { duration: 0.4 } }}
          />

          {/* THIRD BOX — PEERS */}
          <motion.div
            className="absolute z-20 top-[320px] left-[575px] w-[392px]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: showThirdBox ? 1 : 0, y: showThirdBox ? 0 : 20, transition: { duration: 0.45 } }}
          >
            <p className="font-[Gotham] font-[700] text-[16px] mb-3">What did your Peers notice?</p>

            <div className="w-[392px] h-[187px] border border-[#000] rounded-[5px] bg-white shadow-[0_0_24px_#0000004D]">
              <textarea
                value={thirdPerspectiveNotice}
                onChange={(e) => setThirdPerspectiveNotice(e.target.value)}
                placeholder="Ex: Your peers noticed how you collaborated…"
                className="w-full h-full px-[18px] rounded-[5px] py-[18px] outline-none text-[18px] font-[325] font-[Gotham] leading-[165%] text-[#323232] placeholder:text-[#9B9B9B] resize-none"
              />
            </div>

            {stage === 2 && (
              <motion.button
                type="button"
                onClick={handleContinue}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center font-gotham font-medium justify-between w-[305px] h-[57px] bg-[#0D1111] text-white rounded-[20px] px-[20px]"
              >
                Deconstruct what changed
                <span className="ml-3 flex items-center justify-center w-[18px] h-[18px] bg-white text-black rounded-full">
                  <svg width={10} height={10} viewBox="0 0 20 20">
                    <path d="M8 5l4 5-4 5" stroke="#151A1F" strokeWidth="2" />
                  </svg>
                </span>
              </motion.button>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ThinkWhatMadeThisPossibleStep;